import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, signOutAdmin, getCurrentUser } from "@/lib/supabaseClient";
import { 
  Plus, Edit2, Trash2, LogOut, Calendar, 
  AlertTriangle, Zap, CloudLightning, Check, Save, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  insertNamedStorm,
  getCurrentSeason,
  getCurrentSeasonStorms,
  getAllStormsForAdmin,
  updateStormStats,
  fetchStormWarnings,
  insertStormWarning,
  deleteStormWarning,
} from "@/lib/stormsQueries";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_thunder-alert-maps/artifacts/6okjv5b7_UKWX%20LOGO%20NEW%202025.png";

/** Inline admin form for adding a named storm */
function AddStormForm({ onAdded }) {
  const [stormName, setStormName] = useState("");
  const [dateNamed, setDateNamed] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stormName.trim() || !dateNamed) {
      toast.error("Storm name and date are required");
      return;
    }
    try {
      setSubmitting(true);
      await insertNamedStorm({ storm_name: stormName.trim(), date_named: dateNamed, notes: notes.trim() });
      toast.success(`Storm ${stormName.trim()} added successfully`);
      setStormName("");
      setDateNamed("");
      setNotes("");
      if (onAdded) onAdded();
    } catch (err) {
      toast.error(`Failed to add storm: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Storm Name *</label>
          <input
            type="text"
            value={stormName}
            onChange={(e) => setStormName(e.target.value)}
            placeholder="e.g. Ashley"
            className="w-full bg-[#0B1220] border border-white/10 text-[#F9FAFB] text-sm px-3 py-2 rounded-none focus:outline-none focus:border-cyan-500/50 placeholder:text-[#6B7280]"
            required
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Date Named *</label>
          <input
            type="date"
            value={dateNamed}
            onChange={(e) => setDateNamed(e.target.value)}
            className="w-full bg-[#0B1220] border border-white/10 text-[#F9FAFB] text-sm px-3 py-2 rounded-none focus:outline-none focus:border-cyan-500/50"
            required
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes"
            className="w-full bg-[#0B1220] border border-white/10 text-[#F9FAFB] text-sm px-3 py-2 rounded-none focus:outline-none focus:border-cyan-500/50 placeholder:text-[#6B7280]"
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className="bg-cyan-500 text-[#0B1220] hover:bg-cyan-400 font-bold uppercase tracking-wider rounded-none"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-[#0B1220] border-t-transparent rounded-full animate-spin" />
            Adding…
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Storm
          </span>
        )}
      </Button>
    </form>
  );
}


/** Form to edit storm statistics (peak_gust mph, peak_pressure hPa, peak_rainfall mm) */
function StormStatsForm({ storms }) {
  const [selectedId, setSelectedId] = useState("");
  const [peakGust, setPeakGust] = useState("");
  const [peakPressure, setPeakPressure] = useState("");
  const [peakRainfall, setPeakRainfall] = useState("");
  const [saving, setSaving] = useState(false);

  function handleStormChange(id) {
    setSelectedId(id);
    const s = storms.find((x) => x.id === id);
    setPeakGust(s?.peak_gust != null ? String(s.peak_gust) : "");
    setPeakPressure(s?.peak_pressure != null ? String(s.peak_pressure) : "");
    setPeakRainfall(s?.peak_rainfall != null ? String(s.peak_rainfall) : "");
  }

  async function handleSave() {
    if (!selectedId) { toast.error("Select a storm first"); return; }
    const gust = peakGust !== "" ? parseInt(peakGust, 10) : null;
    const pressure = peakPressure !== "" ? parseInt(peakPressure, 10) : null;
    const rainfall = peakRainfall !== "" ? parseInt(peakRainfall, 10) : null;
    if ((gust !== null && isNaN(gust)) || (pressure !== null && isNaN(pressure)) || (rainfall !== null && isNaN(rainfall))) {
      toast.error("Statistics must be valid integers");
      return;
    }
    try {
      setSaving(true);
      await updateStormStats(selectedId, {
        peak_gust: gust,
        peak_pressure: pressure,
        peak_rainfall: rainfall,
      });
      toast.success("Storm statistics saved");
    } catch (err) {
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full bg-[#0B1220] border border-white/10 text-[#F9FAFB] text-sm px-3 py-2 rounded-none focus:outline-none focus:border-cyan-500/50 placeholder:text-[#6B7280]";

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Storm</label>
        <select
          value={selectedId}
          onChange={(e) => handleStormChange(e.target.value)}
          className={inputCls}
        >
          <option value="">— Select storm —</option>
          {storms.map((s) => (
            <option key={s.id} value={s.id}>
              {s.storm_name} ({s.season})
            </option>
          ))}
        </select>
      </div>
      {selectedId && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Peak Gust (mph)</label>
              <input
                type="number"
                value={peakGust}
                onChange={(e) => setPeakGust(e.target.value)}
                placeholder="e.g. 120"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Peak Pressure (hPa)</label>
              <input
                type="number"
                value={peakPressure}
                onChange={(e) => setPeakPressure(e.target.value)}
                placeholder="e.g. 955"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Peak Rainfall (mm)</label>
              <input
                type="number"
                value={peakRainfall}
                onChange={(e) => setPeakRainfall(e.target.value)}
                placeholder="e.g. 80"
                className={inputCls}
              />
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-cyan-500 text-[#0B1220] hover:bg-cyan-400 font-bold uppercase tracking-wider rounded-none"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-[#0B1220] border-t-transparent rounded-full animate-spin" />
                Saving…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Statistics
              </span>
            )}
          </Button>
        </>
      )}
      {!selectedId && (
        <p className="text-[#6B7280] text-sm">Select a storm to edit its statistics.</p>
      )}
    </div>
  );
}

const WARNING_LEVEL_COLOURS = { yellow: "#FFD700", amber: "#FF8C00", red: "#D32F2F" };

/** Section to manage warnings for a selected storm */
function StormWarningsSection({ storms }) {
  const [selectedId, setSelectedId] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [loadingWarnings, setLoadingWarnings] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalLevel, setModalLevel] = useState("yellow");
  const [modalHazard, setModalHazard] = useState("wind");
  const [modalBody, setModalBody] = useState("Met Office");
  const [saving, setSaving] = useState(false);

  async function loadWarnings(id) {
    if (!id) { setWarnings([]); return; }
    setLoadingWarnings(true);
    try {
      const data = await fetchStormWarnings(id);
      setWarnings(data);
    } catch {
      toast.error("Failed to load warnings");
    } finally {
      setLoadingWarnings(false);
    }
  }

  function handleStormChange(id) {
    setSelectedId(id);
    loadWarnings(id);
  }

  async function handleAddWarning() {
    if (!selectedId) return;
    try {
      setSaving(true);
      const inserted = await insertStormWarning({
        storm_id: selectedId,
        level: modalLevel,
        hazard_type: modalHazard,
        issuing_body: modalBody,
      });
      setWarnings((prev) => [...prev, inserted]);
      setShowModal(false);
      toast.success("Warning added");
    } catch (err) {
      toast.error(`Failed to add warning: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteWarning(id) {
    try {
      await deleteStormWarning(id);
      setWarnings((prev) => prev.filter((w) => w.id !== id));
      toast.success("Warning deleted");
    } catch (err) {
      toast.error(`Failed to delete: ${err.message}`);
    }
  }

  const selectCls = "w-full bg-[#0B1220] border border-white/10 text-[#F9FAFB] text-sm px-3 py-2 rounded-none focus:outline-none focus:border-cyan-500/50";

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Storm</label>
        <select
          value={selectedId}
          onChange={(e) => handleStormChange(e.target.value)}
          className={selectCls}
        >
          <option value="">— Select storm —</option>
          {storms.map((s) => (
            <option key={s.id} value={s.id}>
              {s.storm_name} ({s.season})
            </option>
          ))}
        </select>
      </div>

      {selectedId && (
        <>
          {/* Existing warnings */}
          <div className="border border-white/10">
            <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-[#9CA3AF]">Warnings</span>
              <Button
                size="sm"
                onClick={() => setShowModal(true)}
                className="bg-cyan-500 text-[#0B1220] hover:bg-cyan-400 font-bold uppercase tracking-wider rounded-none text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Warning
              </Button>
            </div>
            {loadingWarnings ? (
              <div className="p-4 flex justify-center">
                <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : warnings.length === 0 ? (
              <p className="text-[#6B7280] text-sm p-4 text-center">No warnings recorded.</p>
            ) : (
              <div>
                {warnings.map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0"
                  >
                    <AlertTriangle
                      style={{ color: WARNING_LEVEL_COLOURS[w.level] || "#FFD700" }}
                      className="w-4 h-4 shrink-0"
                    />
                    <span className="flex-1 text-sm text-[#F9FAFB] uppercase font-semibold tracking-wide">
                      {w.level} {w.hazard_type} Warning
                    </span>
                    <span className="text-xs text-[#6B7280]">{w.issuing_body}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteWarning(w.id)}
                      className="text-[#6B7280] hover:text-[#F33D14] rounded-none p-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {!selectedId && (
        <p className="text-[#6B7280] text-sm">Select a storm to manage its warnings.</p>
      )}

      {/* Add Warning Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#111827] border border-white/10 p-6 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#F9FAFB]">Add Warning</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#6B7280] hover:text-[#F9FAFB]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Level</label>
              <select
                value={modalLevel}
                onChange={(e) => setModalLevel(e.target.value)}
                className={selectCls}
              >
                <option value="yellow">Yellow</option>
                <option value="amber">Amber</option>
                <option value="red">Red</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Hazard Type</label>
              <select
                value={modalHazard}
                onChange={(e) => setModalHazard(e.target.value)}
                className={selectCls}
              >
                <option value="wind">Wind</option>
                <option value="rain">Rain</option>
                <option value="snow">Snow</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">Issuing Body</label>
              <select
                value={modalBody}
                onChange={(e) => setModalBody(e.target.value)}
                className={selectCls}
              >
                <option value="Met Office">Met Office</option>
                <option value="Met Éireann">Met Éireann</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleAddWarning}
                disabled={saving}
                className="flex-1 bg-cyan-500 text-[#0B1220] hover:bg-cyan-400 font-bold uppercase tracking-wider rounded-none"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-[#0B1220] border-t-transparent rounded-full animate-spin" />
                    Saving…
                  </span>
                ) : "Save Warning"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-white/10 rounded-none"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [outlooks, setOutlooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [currentSeasonStorms, setCurrentSeasonStorms] = useState([]);
  const [stormsLoading, setStormsLoading] = useState(true);
  const [allStorms, setAllStorms] = useState([]);

  function loadCurrentSeasonStorms() {
    setStormsLoading(true);
    getCurrentSeasonStorms()
      .then(setCurrentSeasonStorms)
      .catch(() => {})
      .finally(() => setStormsLoading(false));
  }

  function loadAllStorms() {
    getAllStormsForAdmin()
      .then(setAllStorms)
      .catch(() => {});
  }

  useEffect(() => {
    getCurrentUser().then((u) => {
      if (!u) {
        navigate("/admin/login");
      } else {
        setUserEmail(u.email || "Admin");
      }
    });
    fetchOutlooks();
    loadCurrentSeasonStorms();
    loadAllStorms();
    const channel = supabase
      .channel("admin-outlooks")
      .on("postgres_changes", { event: "*", schema: "public", table: "outlooks" }, () => {
        fetchOutlooks();
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOutlooks = async () => {
    try {
      const { data, error } = await supabase
        .from("outlooks")
        .select("*")
        .order("outlook_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      setOutlooks(data || []);
    } catch (err) {
      toast.error("Failed to fetch outlooks");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOutAdmin();
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase.from("outlooks").delete().eq("id", deleteTarget);
      if (error) throw error;
      toast.success("Outlook deleted");
      setOutlooks((prev) => prev.filter((o) => o.id !== deleteTarget));
    } catch (err) {
      toast.error("Failed to delete outlook");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1220]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="UKWX Logo" className="h-8 w-auto" />
              <div>
                <span className="font-heading text-sm font-bold tracking-wider text-[#F9FAFB] uppercase">
                  Admin Dashboard
                </span>
                <span className="block text-[10px] text-[#6B7280]">
                  {userEmail}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-xs text-[#9CA3AF] hover:text-[#F9FAFB] uppercase tracking-wider transition-colors"
              >
                View Site
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-white/10 hover:border-[#F33D14]/30 hover:text-[#F33D14] rounded-none text-xs uppercase tracking-wider"
                data-testid="logout-btn"
              >
                <LogOut className="w-3 h-3 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#F9FAFB] uppercase tracking-tight">
              Outlook Management
            </h1>
            <p className="text-[#9CA3AF] text-sm mt-1">
              Create, edit, and manage outlooks
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/outlook/new">
              <Button
                className="bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none"
                data-testid="create-outlook-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Outlook
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#9CA3AF] text-sm uppercase tracking-wider">Loading...</p>
          </div>
        ) : outlooks.length === 0 ? (
          <div className="text-center py-16 border border-white/10 bg-[#111827]">
            <Calendar className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
            <h3 className="font-heading text-xl text-[#F9FAFB] uppercase tracking-wide mb-2">
              No Outlooks Yet
            </h3>
            <p className="text-[#9CA3AF] text-sm mb-6">
              Create your first outlook to get started.
            </p>
            <Link to="/admin/outlook/new">
              <Button className="bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none">
                <Plus className="w-4 h-4 mr-2" />
                Create Outlook
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {outlooks.map((outlook) => {
              const slug = outlook.outlook_date || outlook.id;
              const displayDate = outlook.outlook_date
                ? new Date(outlook.outlook_date + "T00:00:00").toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : new Date(outlook.created_at).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });

              return (
                <div
                  key={outlook.id}
                  className="bg-[#111827] border border-white/10 p-5 flex flex-col md:flex-row gap-4"
                  data-testid={`admin-outlook-${outlook.id}`}
                >
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {outlook.is_active && (
                        <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-[#22C55E] border border-[#22C55E]/30 px-1.5 py-0.5 bg-[#22C55E]/10">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                          Active
                        </span>
                      )}
                      {!outlook.is_published && (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#6B7280] border border-white/10 px-1.5 py-0.5">
                          Draft
                        </span>
                      )}
                      <span className="font-mono text-xs text-[#6B7280] flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {displayDate}
                      </span>
                      {outlook.lightning_activity > 0 && (
                        <span className="font-mono text-xs text-[#9CA3AF] flex items-center gap-1">
                          <Zap className="w-3 h-3 text-[#FBBF24]" />
                          LAI {outlook.lightning_activity}/10
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-base uppercase tracking-wide text-[#F9FAFB] truncate">
                      {outlook.headline || outlook.title}
                    </h3>
                    {outlook.description && (
                      <p className="text-[#9CA3AF] text-xs line-clamp-1">{outlook.description}</p>
                    )}
                  </div>

                  <div className="flex flex-row md:flex-col gap-2 md:justify-center flex-shrink-0">
                    <Link to={`/outlook/${slug}`} target="_blank">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#6B7280] hover:text-[#F9FAFB] rounded-none text-xs uppercase tracking-wider w-full"
                      >
                        View
                      </Button>
                    </Link>
                    <Link to={`/admin/outlook/${outlook.id}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-white/10 hover:border-[#FBBF24]/30 rounded-none text-xs uppercase tracking-wider w-full"
                        data-testid={`edit-outlook-${outlook.id}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1.5" />
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(outlook.id)}
                      className="border-white/10 hover:border-[#F33D14]/30 hover:text-[#F33D14] rounded-none text-xs uppercase tracking-wider"
                      data-testid={`delete-outlook-${outlook.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Named Storms Admin Section */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl pb-12">
        <div className="border-t border-white/5 pt-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <CloudLightning className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="font-heading text-xl font-bold text-[#F9FAFB] uppercase tracking-tight">
                Named Storms — {getCurrentSeason()} Season
              </h2>
              <p className="text-[#9CA3AF] text-sm mt-0.5">Add new named storms and refresh views</p>
            </div>
          </div>

          {/* Add storm form */}
          <div className="bg-[#111827] border border-white/10 p-6 mb-6">
            <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-4">Add Named Storm</h3>
            <AddStormForm onAdded={loadCurrentSeasonStorms} />
          </div>

          {/* Current season storms list */}
          <div className="bg-[#111827] border border-white/10">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-[#9CA3AF]">
                Current Season Storms
              </span>
              {!stormsLoading && currentSeasonStorms.length > 0 && (
                <span className="text-xs text-cyan-400">{currentSeasonStorms.length} storm{currentSeasonStorms.length !== 1 ? "s" : ""}</span>
              )}
            </div>
            {stormsLoading ? (
              <div className="p-6 text-center">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : currentSeasonStorms.length === 0 ? (
              <p className="text-[#6B7280] text-sm p-6 text-center">No storms added for this season yet.</p>
            ) : (
              <div>
                {currentSeasonStorms.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-4 px-5 py-3 border-b border-white/5 last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#F9FAFB]">{s.storm_name}</span>
                      {s.notes && <p className="text-xs text-[#6B7280] truncate">{s.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#6B7280] shrink-0">
                      <Check className="w-3 h-3 text-cyan-500" />
                      {s.date_named ? new Date(s.date_named).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Storm Statistics Section */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl pb-12">
        <div className="border-t border-white/5 pt-8">
          <div className="flex items-center gap-3 mb-6">
            <CloudLightning className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="font-heading text-xl font-bold text-[#F9FAFB] uppercase tracking-tight">
                Storm Statistics
              </h2>
              <p className="text-[#9CA3AF] text-sm mt-0.5">Edit peak gust, pressure and rainfall for a storm</p>
            </div>
          </div>
          <div className="bg-[#111827] border border-white/10 p-6">
            <StormStatsForm storms={allStorms} />
          </div>
        </div>
      </section>

      {/* Storm Warnings Section */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl pb-12">
        <div className="border-t border-white/5 pt-8">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <div>
              <h2 className="font-heading text-xl font-bold text-[#F9FAFB] uppercase tracking-tight">
                Storm Warnings
              </h2>
              <p className="text-[#9CA3AF] text-sm mt-0.5">Attach weather warnings to a named storm</p>
            </div>
          </div>
          <div className="bg-[#111827] border border-white/10 p-6">
            <StormWarningsSection storms={allStorms} />
          </div>
        </div>
      </section>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#F9FAFB] uppercase tracking-wide flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#F33D14]" />
              Delete Outlook
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              Are you sure you want to delete this outlook? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#F33D14] hover:bg-[#F33D14]/80 rounded-none"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

